import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Role, Office } from '../types';

interface UserRolesContextType {
  roles: Role[];
  offices: Office[];
  loading: boolean;
  error: string | null;
  loadUserRoles: (userId: string) => Promise<void>;
  clearRoles: () => void;
  getOfficeById: (officeId: string) => Office | undefined;
  getRolesByOfficeId: (officeId: string) => Role[];
  refreshData: () => Promise<void>;
}

const UserRolesContext = createContext<UserRolesContextType | undefined>(undefined);

export const useUserRoles = () => {
  const context = useContext(UserRolesContext);
  if (context === undefined) {
    throw new Error('useUserRoles must be used within a UserRolesProvider');
  }
  return context;
};

interface UserRolesProviderProps {
  children: ReactNode;
  userId?: string;
}

export const UserRolesProvider: React.FC<UserRolesProviderProps> = ({ children, userId }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasTriedLoading, setHasTriedLoading] = useState(false);

  const loadUserRoles = async (userId: string) => {
    // Prevent duplicate loading
    if (loading) {
      console.log('⚠️ UserRoles: Already loading, skipping duplicate request');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setHasTriedLoading(true);
      console.log('🔄 Loading user roles for userId:', userId);

      // Fetch user document to get roles
      const userDocRef = doc(db, 'Users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      // Fetch roles from the subcollection /Users/{userId}/Roles
      const rolesCollectionRef = collection(db, 'Users', userId, 'Roles');
      const rolesSnapshot = await getDocs(rolesCollectionRef);
      
      console.log('📋 Found user roles:', rolesSnapshot.size);
      console.log('📋 Roles collection path:', `/Users/${userId}/Roles`);

      if (rolesSnapshot.empty) {
        setRoles([]);
        setOffices([]);
        return;
      }

      // Process roles from the subcollection
      console.log('🔍 Processing roles from subcollection...');
      
      const processedRoles: Role[] = [];
      rolesSnapshot.forEach((roleDoc) => {
        const roleData = roleDoc.data();
        console.log('🔍 Processing role document:', roleDoc.id, roleData);
        
        const role: Role = {
          id: roleDoc.id,
          officeID: roleData.officeID || '',
          officeName: roleData.officeName || '',
          officeLogoURL: roleData.officeLogoURL || '',
          officePhone: roleData.officePhone || '',
          lawyerID: roleData.lawyerID || '',
          lawyerName: roleData.lawyerName || '',
          lawyerPhotoURL: roleData.lawyerPhotoURL || '',
          isOfficeCreator: roleData.isOfficeCreator || false,
          isOfficeAdmin: roleData.isOfficeAdmin || false,
          subSpecialityID: roleData.subSpecialityID || '1',
          lawyerTitleID: roleData.lawyerTitleID || '1',
          salaryIndicator: roleData.salaryIndicator || 0,
          salaryValue: roleData.salaryValue || 0,
          profitPercentage: roleData.profitPercentage || 0,
          isArchived: roleData.isArchived || false,
          isDeleted: roleData.isDeleted || false,
          clientsReferences: roleData.clientsReferences || [],
          userPermission: roleData.userPermission || {
            id: '',
            showClientsList: false,
            updateClientProfile: false,
            removeClientFromOffice: false,
            showOfficeList: false,
            updateOfficeProfile: false,
            removeOfficeFromOffice: false,
            showEvents: false,
            showEventsList: false,
            updateEvent: false,
            removeEvent: false,
            showReports: false,
            showReportsList: false,
            updateReport: false,
            removeReport: false,
            showClientFilesList: false,
            updateClientFile: false,
            removeClientFile: false,
            showSessionsList: false,
            updateSession: false,
            deleteSession: false,
            showLawyersList: false,
            updateLawyerProfile: false,
            removeLawyerFromOffice: false,
          },
          createdAt: roleData.createdAt || '',
          updatedAt: roleData.updatedAt || '',
          createdBy: roleData.createdBy || '',
          updatedBy: roleData.updatedBy || '',
        };
        
        processedRoles.push(role);
      });

      setRoles(processedRoles);
      console.log('✅ Loaded roles:', processedRoles.length);

      // Create offices from role data (each role already contains office info)
      const officeMap = new Map<string, Office>();
      
      processedRoles.forEach(role => {
        if (role.officeID && !officeMap.has(role.officeID)) {
          officeMap.set(role.officeID, {
            id: role.officeID,
            name: role.officeName || 'Unknown Office',
            alternativeName: role.officeName || '',
            logoURL: role.officeLogoURL || '',
            phoneNumber: role.officePhone || '',
            email: '', // Not available in role data
            address: '', // Not available in role data
            website: '', // Not available in role data
            description: '', // Not available in role data
            createdAt: role.createdAt || '',
            updatedAt: role.updatedAt || '',
            createdBy: role.createdBy || '',
            updatedBy: role.updatedBy || '',
          } as Office);
        }
      });

      const fetchedOffices = Array.from(officeMap.values());
      setOffices(fetchedOffices);

      console.log('✅ Loaded offices from roles:', fetchedOffices.length);
      console.log('🏢 Office details:', fetchedOffices.map(office => ({ id: office.id, name: office.name })));
      console.log('🎉 User roles and offices loaded successfully');

    } catch (error) {
      console.error('❌ Error loading user roles:', error);
      setError(error instanceof Error ? error.message : 'Failed to load user roles');
    } finally {
      setLoading(false);
    }
  };

  const getOfficeById = (officeId: string): Office | undefined => {
    return offices.find(office => office.id === officeId);
  };

  const getRolesByOfficeId = (officeId: string): Role[] => {
    return roles.filter(role => role.officeID === officeId);
  };

  const refreshData = async () => {
    if (userId) {
      await loadUserRoles(userId);
    }
  };

  const clearRoles = () => {
    console.log('🧹 Debug - UserRolesContext: Clearing roles and offices');
    setRoles([]);
    setOffices([]);
    setLoading(false);
    setError(null);
    console.log('✅ Debug - UserRolesContext: Roles and offices cleared');
  };

  // Clear roles when user logs out
  useEffect(() => {
    if (!userId) {
      console.log('🧹 Debug - UserRolesContext: User logged out, clearing roles');
      clearRoles();
      setHasTriedLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    console.log('🔄 UserRolesProvider useEffect triggered with userId:', userId);
    console.log('🔄 Current roles count:', roles.length);
    console.log('🔄 Current loading state:', loading);
    console.log('🔄 Has tried loading:', hasTriedLoading);
    
    if (userId) {
      // Only try to load roles once per user
      if (!loading && !hasTriedLoading && roles.length === 0) {
        console.log('🔄 Starting to load user roles...');
        loadUserRoles(userId);
      } else if (loading) {
        console.log('⚠️ UserRoles: Already loading, skipping');
      } else if (hasTriedLoading) {
        console.log('⚠️ UserRoles: Already tried loading, skipping');
      } else if (roles.length > 0) {
        console.log('⚠️ UserRoles: Already loaded, skipping');
      }
    } else {
      console.log('⚠️ No userId provided to UserRolesProvider');
    }
  }, [userId, loading, hasTriedLoading]); // Added hasTriedLoading to dependencies

  return (
    <UserRolesContext.Provider value={{
      roles,
      offices,
      loading,
      error,
      loadUserRoles,
      clearRoles,
      getOfficeById,
      getRolesByOfficeId,
      refreshData
    }}>
      {children}
    </UserRolesContext.Provider>
  );
};
